// eslint-disable-next-line max-classes-per-file
import React from 'react';
import Button from '@material-ui/core/Button';
import { Link } from '@ipc/ipc-core';
import { sortValues } from './sortValues';
import fieldFormatter from './fieldFormatter';
import fieldFilters from './fieldFilters';
import ExternalLink from './ExternalLink';

class Column {
  constructor(header, accessor, sortable = true, parameters) {
    this.Header = header;
    this.accessor = accessor;
    this.sortable = sortable;
    this.parameters = parameters;
  }

  headerClassName = 'wordwrap';

  withWidth(width) {
    this.width = width;
    return this;
  }

  withShow(show) {
    this.show = show;
    return this;
  }

  withGetProps(getProps) {
    this.getProps = getProps;
    return this;
  }
}

export default (data) => {
  const fieldFiltersWithData = fieldFilters(data);

  class BooleanColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('boolean');
    }
  }

  class DateColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('date');
    }
  }

  class DateTimeColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('dateTime');
    }
  }

  class TextColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('nonZeroText');
    }
  }

  class TextCommaColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('commaText');
    }
  }

  class NumberColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.sortMethod = sortValues;
      this.className = '-column-align-right';
      this.Cell = fieldFormatter('number');
    }
  }

  class NonZeroNumberColumn extends NumberColumn {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('nonZeroNumber');
    }
  }

  class PercentageColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('percentage');
      this.sortMethod = sortValues;
      this.className = '-column-align-right';
    }
  }

  class FilteredBooleanColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('boolean');
      Object.assign(this, fieldFiltersWithData.applyBooleanFilter(this));
    }
  }

  class FilteredDateColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('date');
      Object.assign(this, fieldFiltersWithData.applyDateFilter(this));
    }
  }

  class FilteredDateWithDayColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('dateWithDay');
      Object.assign(this, fieldFiltersWithData.applyDateWithDayNameFilter(this));
    }
  }

  class FilteredDateTimeColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('dateTime');
      Object.assign(this, fieldFiltersWithData.applyDatetimeFilter(this));
    }
  }

  class FilteredDateTimeWithSecondsColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('dateTimeWithSeconds');
      Object.assign(this, fieldFiltersWithData.applyDatetimeFilter(this));
    }
  }

  class FilteredTextColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('nonZeroText');
      Object.assign(this, fieldFiltersWithData.applyTextFilter(this));
    }
  }

  class FilteredTextCommaColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('commaText');
      Object.assign(this, fieldFiltersWithData.applyTextFilter(this));
    }
  }

  class FilteredNumberColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.sortMethod = sortValues;
      this.className = '-column-align-right';
      this.Cell = fieldFormatter('number');
      Object.assign(this, fieldFiltersWithData.applyNumberFilter(this));
    }
  }

  class FilteredNonZeroNumberColumn extends NumberColumn {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('nonZeroNumber');
      Object.assign(this, fieldFiltersWithData.applyNumberFilter(this));
    }
  }

  class FilteredPercentageColumn extends Column {
    constructor(header, accessor, sortable) {
      super(header, accessor, sortable);
      this.Cell = fieldFormatter('percentage');
      Object.assign(this, fieldFiltersWithData.applyNumberFilter(this));
      this.sortMethod = sortValues;
      this.className = '-column-align-right';
    }
  }

  class RadioButtonColumn extends Column {
    constructor(header, accessor, sortable, parameter) {
      super(header, accessor, sortable);
      this.Cell = (row) => (
        <input type="radio" name={parameter} value={row.original.code} />
      );
    }
  }

  class EditButtonColumn extends Column {
    constructor(header, accessor, sortable, text) {
      super(header, accessor, sortable);
      this.Cell = (row) => (
        <div className="table-buttons">
          <Button variant="contained" size="medium" color="primary">
            {text}
          </Button>
        </div>
      );
    }
  }

  class WarningSignColumn extends Column {
    constructor(header, accessor, sortable, parameters) {
      super(header, accessor, sortable);
      this.Cell = (row) => {
        let warningSign = ' ';
        if (row.original[accessor] > row.original[parameters.compareRight]) {
          warningSign = '⚠️';
        } else if (row.original[accessor] < row.original[parameters.compareLeft]) {
          warningSign = '⛔';
        }

        return warningSign;
      };
    }
  }

  class QuantityWarningSignColumn extends Column {
    constructor(header, accessor, sortable, parameters) {
      super(header, accessor, sortable);
      this.Cell = (row) => {
        let warningSign = ' ';
        if (row.original[parameters.compareRight] !== null && (row.original[parameters.compareRight] < row.original[parameters.compareLeft])) {
          warningSign = '⚠️';
        }
        return warningSign;
      };
    }
  }

  class NavigationColumn {
    navOptionsCallback = () => ({});

    isNavCallback = () => true;

    constructor(column, navOptionsCallback, isNavCallback) {
      Object.assign(this, column);
      this.navOptionsCallback = navOptionsCallback || this.navOptionsCallback;
      this.isNavCallback = isNavCallback || this.isNavCallback;
      this.Cell = (row) => {
        const { value } = row;
        if (value) {
          const isNav = this.isNavCallback(row);
          if (!isNav) {
            return value;
          }
          const {
            path,
            tableId,
            text,
          } = this.navOptionsCallback(row);
          return (
            <div className="table-buttons">
              <Link to={{ pathname: path, state: { tableId }, data: row }}>
                <Button variant="outlined" size="medium" color="primary">
                  {text}
                </Button>
              </Link>
            </div>
          );
        }
        return '';
      };
    }
  }

  class ExternalDespatchLinkColumn {
    constructor(column) {
      Object.assign(this, column);
      this.Cell = (row) => {
        const { value } = row;
        const path = `/en/despatch/${value}`;
        return (
          <ExternalLink host={process.env.atatHost} label={value} path={path} />
        );
      };
    }
  }

  return {
    NavigationColumn,
    BooleanColumn,
    DateTimeColumn,
    DateColumn,
    TextColumn,
    TextCommaColumn,
    EditButtonColumn,
    FilteredNumberColumn,
    FilteredNonZeroNumberColumn,
    FilteredPercentageColumn,
    FilteredBooleanColumn,
    FilteredDateTimeWithSecondsColumn,
    FilteredDateTimeColumn,
    FilteredDateColumn,
    FilteredTextColumn,
    FilteredTextCommaColumn,
    NumberColumn,
    PercentageColumn,
    NonZeroNumberColumn,
    WarningSignColumn,
    RadioButtonColumn,
    QuantityWarningSignColumn,
    ExternalDespatchLinkColumn,
    FilteredDateWithDayColumn,
  };
};
